import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Ticket } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultValidUntil = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

export const AdminPromoCodes = () => {
  const [promos, setPromos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', discount_percent: 10, valid_until: defaultValidUntil(), min_purchase: 0 });

  const fetchPromos = async () => {
    try { const { data } = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true }); setPromos(data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { fetchPromos(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: form.code.toUpperCase().trim(),
        discount_percent: parseInt(form.discount_percent, 10),
        valid_until: new Date(`${form.valid_until}T23:59:59`).toISOString(),
        min_purchase: parseFloat(form.min_purchase) || 0,
      }, { withCredentials: true });
      toast.success('Code promo créé');
      setShowForm(false);
      setForm({ code: '', discount_percent: 10, valid_until: defaultValidUntil(), min_purchase: 0 });
      fetchPromos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    try { await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true }); toast.success('Code supprimé'); fetchPromos(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="bg-white rounded-sm border border-border p-6">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-2xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>Codes promo</h2>
        <button onClick={() => setShowForm(true)} className="btn-primary px-4 py-2 rounded-sm font-medium flex items-center gap-2" data-testid="add-promo-button">
          <Plus className="w-4 h-4" /> Nouveau code
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Les clients saisissent le code à l'étape paiement. La réduction s'applique en pourcentage sur le sous-total.</p>

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-4 bg-surface rounded-sm space-y-3" data-testid="promo-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Créer un code promo</h3>
            <button type="button" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Code *</label>
              <input type="text" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 border border-border rounded-sm font-mono uppercase" placeholder="BIENVENUE10" data-testid="promo-code-input" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Réduction (%) *</label>
              <input type="number" min="1" max="100" required value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm" data-testid="promo-discount-input" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Valable jusqu'au *</label>
              <input type="date" required value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm" data-testid="promo-valid-until-input" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Montant minimum (€)</label>
              <input type="number" min="0" step="0.01" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-sm" data-testid="promo-min-purchase-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary px-4 py-2 rounded-sm font-medium" data-testid="submit-promo-button">Créer</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary px-4 py-2 rounded-sm font-medium">Annuler</button>
          </div>
        </form>
      )}

      {promos.length === 0 ? (
        <p className="text-muted-foreground" data-testid="promo-empty">Aucun code promo pour le moment</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 pr-4">Code</th><th className="py-2 pr-4">Réduction</th><th className="py-2 pr-4">Minimum</th>
                <th className="py-2 pr-4">Expire le</th><th className="py-2 pr-4">Utilisations</th><th className="py-2 pr-4">Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => (
                <tr key={p.code} className="border-b border-border" data-testid={`promo-row-${p.code}`}>
                  <td className="py-2 pr-4 font-mono font-semibold flex items-center gap-2"><Ticket className="w-4 h-4 text-brand-orange" />{p.code}</td>
                  <td className="py-2 pr-4 font-bold text-brand-orange">-{p.discount_percent} %</td>
                  <td className="py-2 pr-4">{p.min_purchase ? `${p.min_purchase.toFixed(2)} €` : '—'}</td>
                  <td className="py-2 pr-4">{new Date(p.valid_until).toLocaleDateString('fr-FR')}</td>
                  <td className="py-2 pr-4">{p.uses}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${p.expired ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-800'}`}>
                      {p.expired ? 'Expiré' : 'Actif'}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => remove(p.code)} className="p-1.5 border border-border rounded-sm hover:border-destructive hover:text-destructive" data-testid={`delete-promo-${p.code}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
